import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { useLayoutEffect, useMemo, useRef } from "react";
import { Box, Button } from "@mui/material";
import { EMobileCandidateLists, translate } from "@sequentech/ui-core";
import { useTranslation } from "react-i18next";
import { ESlateSelectionStatus, getSlateMembers, getSlateSelectionSummary, } from "../../services/SlateSelection";
import { selectBallotSelectionByElectionId } from "../../store/ballotSelections/ballotSelectionsSlice";
import { useAppSelector } from "../../store/hooks";
import { SlateCandidateList } from "./SlateCandidateList";
import { SlateChooser } from "./SlateChooser";
import { SlateMember } from "./SlateMember";
import { SlateSelectionStatus } from "./SlateSelectionStatus";
/**
 * The slates of the ballot with what the voter currently holds of each one:
 * the selected members are marked and each slate says how much of it is
 * selected. Everything is read from the candidate selections of the ballot.
 */
export var SlateSelectionChooser = function (_a) {
    var ballotStyle = _a.ballotStyle, slates = _a.slates, defaultLanguage = _a.defaultLanguage, onEditSelections = _a.onEditSelections, renderApplyAction = _a.renderApplyAction, renderCoverage = _a.renderCoverage;
    var _b = useTranslation(), t = _b.t, i18n = _b.i18n;
    var selection = useAppSelector(selectBallotSelectionByElectionId(ballotStyle.election_id));
    var summaries = useMemo(function () {
        return new Map(slates.slates.map(function (slate) { return [
            slate.id,
            getSlateSelectionSummary(getSlateMembers(slate), selection),
        ]; }));
    }, [slates, selection]);
    return (_jsx(SlateChooser, { slates: slates, defaultLanguage: defaultLanguage, renderCoverage: renderCoverage, renderSummary: function (slate) {
            var summary = summaries.get(slate.id);
            return summary ? _jsx(SlateSelectionStatus, { summary: summary }) : null;
        }, renderMemberLists: function (slate, lists) { return (_jsx(SlateCandidateList, { electionId: ballotStyle.election_id, slateId: slate.id, defaultExpanded: slates.mobileCandidateLists === EMobileCandidateLists.EXPANDED, children: lists })); }, renderMember: function (slate, _slateContest, candidate) {
            var _a, _b;
            return (_jsx(SlateMember, { candidateId: candidate.id, selected: (_b = (_a = summaries.get(slate.id)) === null || _a === void 0 ? void 0 : _a.selectedMemberIds.includes(candidate.id)) !== null && _b !== void 0 ? _b : false, children: translate(candidate, "name", i18n.language) }));
        }, renderActions: function (slate) {
            var _a;
            return (_jsx(SlateCardActions, { isFullySelected: ((_a = summaries.get(slate.id)) === null || _a === void 0 ? void 0 : _a.status) === ESlateSelectionStatus.ALL, onEditSelections: onEditSelections, children: renderApplyAction === null || renderApplyAction === void 0 ? void 0 : renderApplyAction(slate) }));
        } }));
};
/**
 * Selecting a slate that is already fully selected would change nothing, so
 * the card offers to edit the selections instead. The apply action stays
 * mounted with its button hidden, which keeps its announcement of the
 * selection that has just been made.
 */
var SlateCardActions = function (_a) {
    var isFullySelected = _a.isFullySelected, onEditSelections = _a.onEditSelections, children = _a.children;
    var t = useTranslation().t;
    var containerRef = useRef(null);
    var editRef = useRef(null);
    useLayoutEffect(function () {
        var _a, _b;
        if (isFullySelected && ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement))) {
            (_b = editRef.current) === null || _b === void 0 ? void 0 : _b.focus();
        }
    }, [isFullySelected]);
    return (_jsxs(Box, { className: "slate-card-actions", ref: containerRef, sx: isFullySelected ? { "& .slate-apply-button": { display: "none" } } : undefined, children: [isFullySelected ? (_jsx(Button, { className: "slate-edit-selections-button", variant: "secondary", ref: editRef, onClick: onEditSelections, children: t("slates.selection.edit") })) : null, children] }));
};
